import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { getBoruProfilUretimGunlukAnaliz } from "../../../api/uretim/boruProfilDaily";

const todayStr = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const INITIAL_DATE = todayStr();

const formatNumber = (value, maximumFractionDigits = 2) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "0";
  }

  return numberValue.toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
};

function BoruProfilUretimGunlukAnaliz() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* === DATA STATE === */
  const [genel, setGenel] = useState(null);
  const [operatorBazli, setOperatorBazli] = useState([]);
  const [vardiyaBazli, setVardiyaBazli] = useState([]);
  const [firmaBazli, setFirmaBazli] = useState([]);
  const [filterInfo, setFilterInfo] = useState(null);

  /* === FİLTRE === */
  const [startDate, setStartDate] = useState(INITIAL_DATE);
  const [endDate, setEndDate] = useState(INITIAL_DATE);
  const [makina, setMakina] = useState("");

  /* === API LOAD === */
  const loadData = useCallback(
    async (filters = {}) => {
      const selectedStartDate =
        filters.startDate ?? startDate;

      const selectedEndDate =
        filters.endDate ?? endDate;

      const selectedMakina =
        filters.makina ?? makina;

      if (!selectedStartDate || !selectedEndDate) {
        setError(
          "Başlangıç ve bitiş tarihini seçmelisiniz."
        );
        return;
      }

      if (selectedStartDate > selectedEndDate) {
        setError(
          "Başlangıç tarihi bitiş tarihinden büyük olamaz."
        );
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res =
          await getBoruProfilUretimGunlukAnaliz(
            selectedStartDate,
            selectedEndDate,
            selectedMakina
          );

        const data = res?.data?.data || {};

        setFilterInfo(data?.filter || null);
        setGenel(data?.genel || {});

        setOperatorBazli(
          Array.isArray(data?.operatorBazli)
            ? data.operatorBazli
            : []
        );

        setVardiyaBazli(
          Array.isArray(data?.vardiyaBazli)
            ? data.vardiyaBazli
            : []
        );

        setFirmaBazli(
          Array.isArray(data?.firmaBazli)
            ? data.firmaBazli
            : []
        );
      } catch (err) {
        console.error(
          "Günlük üretim analiz hata:",
          err
        );

        setError(
          err?.response?.data?.message ||
            "Günlük üretim analiz verileri alınamadı."
        );

        setFilterInfo(null);
        setGenel({});
        setOperatorBazli([]);
        setVardiyaBazli([]);
        setFirmaBazli([]);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, makina]
  );

  useEffect(() => {
    loadData({
      startDate: INITIAL_DATE,
      endDate: INITIAL_DATE,
      makina: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    loadData({
      startDate,
      endDate,
      makina,
    });
  };

  /* === KPI === */
  const kpi = useMemo(() => {
    const toplamTonaj = Number(
      genel?.toplamTonaj || 0
    );

    const toplamFiili = Number(
      genel?.toplamFiili || 0
    );

    const toplamDurus = Number(
      genel?.toplamDurus || 0
    );

    return {
      tonaj: toplamTonaj,
      iskarta: Number(
        genel?.toplamIskarta || 0
      ),
      paket: Number(
        genel?.toplamPaket || 0
      ),
      yarimBag: Number(
        genel?.toplamYarimBag || 0
      ),
      fiili: toplamFiili,
      durus: toplamDurus,
      sure:
        Number(genel?.toplamSure) ||
        toplamFiili + toplamDurus,
      verim: Number(genel?.verim || 0),
      kayitSayisi: Number(
        genel?.toplamKayitSayisi || 0
      ),
    };
  }, [genel]);

  /* === VARDİYA MAP === */
  const vardiyaMap = useMemo(() => {
    const emptyShift = {
      tonaj: 0,
      iskarta: 0,
      paket: 0,
      yarimBag: 0,
      fiili: 0,
      durus: 0,
      toplamSure: 0,
      verim: 0,
      kayitSayisi: 0,
    };

    const map = {
      GUNDUZ: { ...emptyShift },
      GECE: { ...emptyShift },
    };

    vardiyaBazli.forEach((item) => {
      const vardiya = String(
        item?.vardiya || ""
      )
        .trim()
        .toUpperCase();

      if (
        vardiya !== "GUNDUZ" &&
        vardiya !== "GECE"
      ) {
        return;
      }

      const fiili = Number(item?.fiili || 0);
      const durus = Number(item?.durus || 0);

      map[vardiya] = {
        ...map[vardiya],
        ...item,
        fiili,
        durus,
        kayitSayisi: Number(
          item?.kayitSayisi || 0
        ),
        toplamSure:
          Number(item?.toplamSure) ||
          fiili + durus,
      };
    });

    return map;
  }, [vardiyaBazli]);

  const renderVardiyaBlock = (
    title,
    data,
    badgeVariant
  ) => {
    const tonaj = Number(data?.tonaj || 0);
    const iskarta = Number(
      data?.iskarta || 0
    );
    const paket = Number(data?.paket || 0);
    const yarimBag = Number(
      data?.yarimBag || 0
    );
    const fiili = Number(data?.fiili || 0);
    const durus = Number(data?.durus || 0);
    const kayitSayisi = Number(
      data?.kayitSayisi || 0
    );

    const toplamSure =
      Number(data?.toplamSure) ||
      fiili + durus;

    const calismaOrani =
      toplamSure > 0
        ? Math.round(
            (fiili / toplamSure) * 100
          )
        : 0;

    const kgDakika =
      Number(data?.verim) ||
      (fiili > 0 ? tonaj / fiili : 0);

    const hasZeroProductionRecord =
      kayitSayisi > 0 &&
      tonaj === 0 &&
      paket === 0 &&
      fiili === 0 &&
      durus === 0;

    return (
      <Card className="p-3 shadow-sm h-100">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold m-0">
            {title}
          </h5>

          <Badge
            bg={badgeVariant}
            className="p-2"
          >
            {formatNumber(tonaj)} Kg
          </Badge>
        </div>

        <Row className="g-3 mb-3">
          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Kayıt Sayısı
            </div>
            <div className="fw-bold">
              {formatNumber(kayitSayisi, 0)}
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Paket
            </div>
            <div className="fw-bold">
              {formatNumber(paket, 0)}
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Yarım Bağ
            </div>
            <div className="fw-bold">
              {formatNumber(yarimBag, 0)}
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Iskarta
            </div>
            <div className="fw-bold">
              {formatNumber(iskarta)} Kg
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Fiili
            </div>
            <div className="fw-bold">
              {formatNumber(fiili, 0)} dk
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Duruş
            </div>
            <div className="fw-bold">
              {formatNumber(durus, 0)} dk
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Toplam Süre
            </div>
            <div className="fw-bold">
              {formatNumber(toplamSure, 0)} dk
            </div>
          </Col>

          <Col xs={6} lg={4}>
            <div className="text-muted small">
              Verim
            </div>
            <div className="fw-bold">
              {formatNumber(kgDakika)} Kg/Dk
            </div>
          </Col>
        </Row>

        <div>
          <div className="d-flex justify-content-between small mb-1">
            <span>Çalışma Oranı</span>
            <strong>
              %{calismaOrani}
            </strong>
          </div>

          <ProgressBar
            now={calismaOrani}
            variant={
              calismaOrani >= 80
                ? "success"
                : calismaOrani >= 60
                ? "warning"
                : "danger"
            }
          />
        </div>

        {hasZeroProductionRecord && (
          <Alert
            variant="warning"
            className="mt-3 mb-0 py-2"
          >
            Bu vardiyada {kayitSayisi} kayıt mevcut;
            ancak üretim, paket, fiili süre ve duruş
            değerleri 0 olarak geliyor.
          </Alert>
        )}
      </Card>
    );
  };

  return (
    <Layout>
      <style>{`
        .analysis-table-wrapper {
          width: 100%;
          overflow-x: auto;
        }

        .analysis-kpi-card {
          height: 100%;
          border-radius: 12px;
        }

        .analysis-kpi-value {
          font-size: 1.65rem;
          line-height: 1.2;
        }

        .zero-data-row td {
          background-color: #fff3cd !important;
        }

        @media (max-width: 768px) {
          .analysis-page-title {
            font-size: 1.35rem;
          }

          .analysis-kpi-value {
            font-size: 1.25rem;
          }

          .analysis-table-wrapper table {
            min-width: 1050px;
            font-size: 0.8rem;
          }

          .analysis-table-wrapper th,
          .analysis-table-wrapper td {
            white-space: nowrap;
          }
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h3 className="analysis-page-title fw-bold mb-0">
          Günlük Üretim Analizi
        </h3>

        {filterInfo && (
          <Badge bg="secondary" className="p-2">
            Toplam Kayıt:{" "}
            {formatNumber(
              filterInfo?.rowCount || 0,
              0
            )}
          </Badge>
        )}
      </div>

      {/* === FİLTRE === */}
      <Card className="p-3 shadow-sm mb-4">
        <Row className="g-3 align-items-end">
          <Col xs={12} md={3}>
            <Form.Label>
              Başlangıç Tarihi
            </Form.Label>

            <Form.Control
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
            />
          </Col>

          <Col xs={12} md={3}>
            <Form.Label>
              Bitiş Tarihi
            </Form.Label>

            <Form.Control
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) =>
                setEndDate(e.target.value)
              }
            />
          </Col>

          <Col xs={12} md={3}>
            <Form.Label>Makina</Form.Label>

            <Form.Select
              value={makina}
              onChange={(e) =>
                setMakina(e.target.value)
              }
            >
              <option value="">Tümü</option>
              <option value="1">
                Makina 1
              </option>
              <option value="2">
                Makina 2
              </option>
              <option value="3">
                Makina 3
              </option>
              <option value="4">
                Makina 4
              </option>
            </Form.Select>
          </Col>

          <Col xs={12} md={3}>
            <Button
              className="w-100"
              onClick={handleFilter}
              disabled={loading}
            >
              {loading
                ? "Yükleniyor..."
                : "Filtrele"}
            </Button>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* === KPI === */}
          <Row className="g-3 mb-4">
            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Toplam Üretim
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.tonaj)} Kg
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Toplam Iskarta
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.iskarta)} Kg
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Toplam Süre
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.sure, 0)} dk
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Toplam Paket
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.paket, 0)}
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Yarım Bağ
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.yarimBag, 0)}
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Ortalama Verim
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(kpi.verim)} Kg/Dk
                </div>
              </Card>
            </Col>

            <Col xs={6} xl>
              <Card className="analysis-kpi-card p-3 shadow-sm">
                <div className="text-muted small">
                  Kayıt Sayısı
                </div>

                <div className="analysis-kpi-value fw-bold">
                  {formatNumber(
                    kpi.kayitSayisi,
                    0
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* === VARDİYA === */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={6}>
              {renderVardiyaBlock(
                "Gündüz Vardiyası",
                vardiyaMap.GUNDUZ,
                "success"
              )}
            </Col>

            <Col xs={12} md={6}>
              {renderVardiyaBlock(
                "Gece Vardiyası",
                vardiyaMap.GECE,
                "primary"
              )}
            </Col>
          </Row>

          {/* === OPERATÖR === */}
          <Card className="p-3 shadow-sm mb-4">
            <h5 className="fw-bold mb-3">
              Operatör Bazlı Özet
            </h5>

            <div className="analysis-table-wrapper">
              <Table
                bordered
                hover
                responsive
                className="mb-0"
              >
                <thead>
                  <tr>
                    <th>Operatör</th>
                    <th>Kayıt Sayısı</th>
                    <th>Tonaj</th>
                    <th>Iskarta</th>
                    <th>Paket</th>
                    <th>Yarım Bağ</th>
                    <th>Duruş</th>
                    <th>Fiili</th>
                    <th>Toplam Süre</th>
                    <th>Verim</th>
                  </tr>
                </thead>

                <tbody>
                  {operatorBazli.map(
                    (operator, index) => {
                      const operatorTonaj = Number(
                        operator?.tonaj || 0
                      );

                      const operatorKayitSayisi =
                        Number(
                          operator?.kayitSayisi || 0
                        );

                      const isZeroData =
                        operatorKayitSayisi > 0 &&
                        operatorTonaj === 0 &&
                        Number(
                          operator?.paket || 0
                        ) === 0 &&
                        Number(
                          operator?.fiili || 0
                        ) === 0 &&
                        Number(
                          operator?.durus || 0
                        ) === 0;

                      return (
                        <tr
                          key={`${operator.operator}-${index}`}
                          className={
                            isZeroData
                              ? "zero-data-row"
                              : ""
                          }
                        >
                          <td>
                            {operator.operator ||
                              "-"}
                          </td>

                          <td>
                            {formatNumber(
                              operatorKayitSayisi,
                              0
                            )}
                          </td>

                          <td>
                            {formatNumber(
                              operator.tonaj
                            )}{" "}
                            Kg
                          </td>

                          <td>
                            {formatNumber(
                              operator.iskarta
                            )}{" "}
                            Kg
                          </td>

                          <td>
                            {formatNumber(
                              operator.paket,
                              0
                            )}
                          </td>

                          <td>
                            {formatNumber(
                              operator.yarimBag,
                              0
                            )}
                          </td>

                          <td>
                            {formatNumber(
                              operator.durus,
                              0
                            )}{" "}
                            dk
                          </td>

                          <td>
                            {formatNumber(
                              operator.fiili,
                              0
                            )}{" "}
                            dk
                          </td>

                          <td>
                            {formatNumber(
                              operator.toplamSure,
                              0
                            )}{" "}
                            dk
                          </td>

                          <td>
                            {formatNumber(
                              operator.verim
                            )}{" "}
                            Kg/Dk
                          </td>
                        </tr>
                      );
                    }
                  )}

                  {operatorBazli.length ===
                    0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center text-muted"
                      >
                        Operatör verisi
                        bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* === FİRMA === */}
          <Card className="p-3 shadow-sm">
            <h5 className="fw-bold mb-3">
              Firma Bazlı Üretim
            </h5>

            <div className="analysis-table-wrapper">
              <Table
                bordered
                hover
                responsive
                className="mb-0"
              >
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th>Kayıt Sayısı</th>
                    <th>Tonaj</th>
                    <th>Iskarta</th>
                    <th>Paket</th>
                    <th>Yarım Bağ</th>
                    <th>Duruş</th>
                    <th>Fiili</th>
                    <th>Toplam Süre</th>
                    <th>Verim</th>
                  </tr>
                </thead>

                <tbody>
                  {firmaBazli.map(
                    (firma, index) => (
                      <tr
                        key={`${firma.firma}-${index}`}
                      >
                        <td>
                          {firma.firma || "-"}
                        </td>

                        <td>
                          {formatNumber(
                            firma.kayitSayisi,
                            0
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            firma.tonaj
                          )}{" "}
                          Kg
                        </td>

                        <td>
                          {formatNumber(
                            firma.iskarta
                          )}{" "}
                          Kg
                        </td>

                        <td>
                          {formatNumber(
                            firma.paket,
                            0
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            firma.yarimBag,
                            0
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            firma.durus,
                            0
                          )}{" "}
                          dk
                        </td>

                        <td>
                          {formatNumber(
                            firma.fiili,
                            0
                          )}{" "}
                          dk
                        </td>

                        <td>
                          {formatNumber(
                            firma.toplamSure,
                            0
                          )}{" "}
                          dk
                        </td>

                        <td>
                          {formatNumber(
                            firma.verim
                          )}{" "}
                          Kg/Dk
                        </td>
                      </tr>
                    )
                  )}

                  {firmaBazli.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center text-muted"
                      >
                        Firma verisi bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
}

export default BoruProfilUretimGunlukAnaliz;
